import { useState, useRef, useEffect } from 'react';
import { Building2, Check, ChevronsUpDown } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { cn } from '@/lib/utils';

/** Workspace switcher for the shell topbar. Renders nothing without orgs. */
export function OrgSwitcher() {
  const user = useAuthStore((s) => s.user);
  const activeOrgId = useAuthStore((s) => s.activeOrgId);
  const setActiveOrg = useAuthStore((s) => s.setActiveOrg);

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const orgs = user?.organizations ?? [];
  const active = orgs.find((o) => o.id === activeOrgId) ?? orgs[0];

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (orgs.length === 0 || !active) return null;

  const canSwitch = orgs.length > 1;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={!canSwitch}
        aria-haspopup={canSwitch ? 'listbox' : undefined}
        aria-expanded={canSwitch ? open : undefined}
        onClick={() => canSwitch && setOpen((v) => !v)}
        className={cn(
          'flex max-w-[16rem] items-center gap-2 rounded-lg border border-grey-200 bg-white px-3 py-1.5 text-left transition-colors',
          canSwitch ? 'hover:bg-grey-50 focus:outline-none focus:ring-2 focus:ring-accent/20' : 'cursor-default',
        )}
      >
        <Building2 className="h-4 w-4 shrink-0 text-grey-500" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-secondary">{active.name}</span>
        {canSwitch && <ChevronsUpDown className="h-4 w-4 shrink-0 text-grey-400" aria-hidden />}
      </button>

      {open && canSwitch && (
        <div
          role="listbox"
          aria-label="Switch workspace"
          className="absolute right-0 z-50 mt-1 w-64 overflow-hidden rounded-lg border border-grey-200 bg-white shadow-elevated"
        >
          <p className="px-3 pt-2 pb-1 text-caption font-bold uppercase tracking-wider text-grey-400">
            Switch workspace
          </p>
          {orgs.map((org) => (
            <button
              key={org.id}
              type="button"
              role="option"
              aria-selected={org.id === active.id}
              onClick={() => {
                setActiveOrg(org.id);
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-secondary hover:bg-grey-50"
            >
              <Building2 className="h-4 w-4 shrink-0 text-grey-400" aria-hidden />
              <span className="min-w-0 flex-1 truncate">{org.name}</span>
              {org.id === active.id && <Check className="h-4 w-4 shrink-0 text-accent" aria-hidden />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
